import React, {useState, useEffect} from 'react'
import {useField} from 'formik'

export interface AsyncSearchInputProps<T> {
  name: string
  label?: string
  labelPosition?: 'top' | 'left'
  wrapperClassName?: string
  fetchHandler: (query: string) => Promise<T[]>
  onSelect?: (item: T) => void
  onClear?: () => void
  idField?: keyof T
  displayField?: keyof T | ((item: T) => string)
  placeholder?: string
  debounceMs?: number
  clearValue?: T
  modalId?: string
  modalLabel?: string
  showModal?: boolean  // Yeni prop
  onModalToggle?: (show: boolean) => void  // Yeni prop
}

export default function AsyncSearchInput<T>({
  name,
  label,
  labelPosition = 'top',
  wrapperClassName = '',
  fetchHandler,
  onSelect,
  onClear,
  idField = 'id' as keyof T,
  displayField,
  placeholder = 'Arama yapın...',
  debounceMs = 500,
  clearValue,
  modalId,
  modalLabel,
  showModal = false,
  onModalToggle,
}: AsyncSearchInputProps<T>) {
  const [field, meta, helpers] = useField(name)
  const {value} = field
  const {setValue} = helpers

  const [query, setQuery] = useState('')
  const [results, setResults] = useState<T[]>([])
  const [loading, setLoading] = useState(false)
  const [userSelected, setUserSelected] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const [searched, setSearched] = useState(false)

  // İlk renderda query'yi groupName'e bağla
  useEffect(() => {
    if (value && typeof value === 'object') {
      const display = getDisplayValue(value)
      if (display !== query) setQuery(display)
    }
  }, [value, query])

  useEffect(() => {
    if (userSelected) {
      setUserSelected(false)
      return
    }

    const timer = setTimeout(() => {
      if (query.length > 2) handleSearch(query)
      else {
        setResults([])
        setSearched(false)
      }
    }, debounceMs)

    return () => clearTimeout(timer)
  }, [query, debounceMs, userSelected])

  const handleSearch = async (q: string) => {
    try {
      setLoading(true)
      const data = await fetchHandler(q)
      setResults(data || [])
      setSearched(true)
    } catch (error) {
      console.error('Arama hatası:', error)
      setResults([])
      setSearched(true)
    } finally {
      setLoading(false)
    }
  }

  const handleSelect = (item: T) => {
    setIsTyping(false)
    setUserSelected(true)
    onSelect?.(item)
    const display = getDisplayValue(item)
    setQuery(display)
    setValue(item)
    setResults([])
    setSearched(false)
  }

  const handleClear = () => {
    setIsTyping(false)
    setQuery('')
    setResults([])
    setSearched(false)
    setValue(clearValue)
    onClear?.()
  }

  const getDisplayValue = (item: any): string => {
    if (!item) return ''

    if (typeof displayField === 'function') return displayField(item)

    if (typeof displayField === 'string') {
      return item?.[displayField] ?? ''
    }

    // fallback: ilk string alan
    const key = Object.keys(item).find((k) => typeof item[k] === 'string')
    return key ? item[key] : ''
  }

  const isInvalid = meta.touched && meta.error

  const renderLabel = () => {
    if (!label) return null

    return (
      <label className={`form-label fw-bold ${labelPosition === 'left' ? 'mb-0' : ''}`}>
        {label}
      </label>
    )
  }

  const handleModalOpen = () => {
    onModalToggle?.(true)
  }

  const inputElement = (
    <div className={labelPosition === 'left' && label ? 'flex-grow-1' : 'w-100'}>
      <div className='position-relative'>
        <div className='input-group'>
           {modalId && (
            <button
              type='button'
              className='btn btn-primary btn-active-light-primary'
              onClick={handleModalOpen}
            >
              <i className='bi bi-search fs-2'></i>
              {modalLabel}
            </button>
          )}
          <input
            name={name}
            type='text'
            className={`form-control ${isInvalid ? 'is-invalid' : ''}`}
            placeholder={placeholder}
            value={query}
            onChange={(e) => {
              setIsTyping(true)
              setQuery(e.target.value)
            }}
          />

          <button
            type='button'
            className='btn btn-icon btn-light btn-active-light-primary'
            onClick={handleClear}
          >
             <i className='bi bi-trash fs-2'></i>
          </button>
        </div>

        {isInvalid && <div className='invalid-feedback'>{meta.error}</div>}
      </div>

      {loading && (
        <div className='position-absolute bg-white border w-100 p-2'>
          <small>Yükleniyor...</small>
        </div>
      )}

      {results.length > 0 && (
        <ul
          className='list-group position-absolute w-50 shadow-sm'
          style={{zIndex: 10, maxHeight: 100, overflowY: 'auto'}}
        >
          {isTyping &&
            results.map((item, idx) => (
              <li
                key={idx}
                className='list-group-item list-group-item-action'
                onClick={() => handleSelect(item)}
                style={{cursor: 'pointer'}}
              >
                {getDisplayValue(item)}
              </li>
            ))}
        </ul>
      )}

      {!loading && isTyping && searched && results.length === 0 && query.length > 2 && (
        <div
          className='list-group position-absolute w-50 shadow-sm'
          style={{zIndex: 10}}
        >
          <div className='list-group-item text-muted'>Kayıt bulunamadı</div>
        </div>
      )}
    </div>
  )

  if (labelPosition === 'left' && label) {
    return (
      <div className={`${wrapperClassName} h-100`}>
        <div className='d-flex align-items-start gap-3 h-100'>
          <div className='flex-shrink-0 pt-2'>{renderLabel()}</div>
          {inputElement}
        </div>
      </div>
    )
  }

  return (
    <div className={`${wrapperClassName} h-100`}>
      {renderLabel()}
      {inputElement}
    </div>
  )
}
